import { FC, memo, useCallback, useEffect, useRef } from 'react';
import { Box, Button, Flex } from 'theme-ui';
import { useDispatch, useSelector } from 'react-redux';
import { getScenes, setCurrentSceneByTimeline } from 'modules/scenes';
import Icon, { IconTypes } from 'shared/Icon';
import { throttle } from 'throttle-debounce';
import { TimeSlider } from './shared/TimeSlider';
import { AudioPanel } from './timeline/AudioPreview';
import analytics from 'utils/analytics';
import { playingSelector, timeSelector, usePlayer } from 'utils/PlayerContext';

type TimelineProps = {
  time: number;
  duration: number;
  visible: boolean;
  playing: boolean;
  handleChange: (time: number) => void;
  pauseHandler: () => void;
  playHandler: () => void;
  handlePointerDown: () => void;
};

const TimelineBase: FC<TimelineProps> = memo(
  ({
    handleChange,
    visible,
    time,
    duration,
    playing,
    playHandler,
    pauseHandler,
    handlePointerDown
  }) => {
    return visible ? (
      <Flex
        sx={{
          bg: 'background',
          marginLeft: '8px',
          px: 6,
          py: '14px',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px'
        }}
      >
        <Button
          data-test={playing ? 'timeline-button-pause' : 'timeline-button-play'}
          sx={{ px: 4, py: 0, width: 56, height: 40, justifyContent: 'center' }}
          mr={3}
          onClick={playing ? pauseHandler : playHandler}
        >
          <Icon name={playing ? 'pause-editor' : 'play-editor'} type={IconTypes.White} />
        </Button>
        <Box sx={{ width: '100%', position: 'relative', mx: 4, mr: 0 }}>
          <AudioPanel />
          <TimeSlider
            min={0}
            max={duration}
            value={time}
            onChange={handleChange}
            onPointerDown={handlePointerDown}
            sx={{ width: '100%' }}
          />
        </Box>
      </Flex>
    ) : (
      <div />
    );
  }
);

export default () => {
  const time = usePlayer(timeSelector);
  const playing = usePlayer(playingSelector);
  const player = usePlayer();
  const scenes = useSelector(getScenes);

  const dispatch = useDispatch();

  const lastScene = scenes[scenes.length - 1];
  const duration = lastScene.start + lastScene.duration;

  const visible = scenes.some(({ duration: sceneDuration }) => sceneDuration > 0);

  const setTime = (value: number) => {
    player.setTime(value);
  };

  const pauseHandler: TimelineProps['pauseHandler'] = () => {
    analytics.timelineAction({
      action: 'pause'
    });
    player.pause();
    dispatch(setCurrentSceneByTimeline());
  };

  const playHandler: TimelineProps['playHandler'] = () => {
    analytics.timelineAction({
      action: 'play'
    });
    player.enableSound();
    player.play();
  };

  const pointerUpHandler = () => dispatch(setCurrentSceneByTimeline());

  const handleChange = useCallback(throttle(50, setTime), [setTime]);

  const timeoutBeforePlaying = useRef(-1);
  const werePlaying = useRef(playing);

  useEffect(() => () => window.clearTimeout(timeoutBeforePlaying.current));

  const handlePointerUp = () => {
    pointerUpHandler();

    if (werePlaying.current) {
      timeoutBeforePlaying.current = window.setTimeout(playHandler, 200);
    }

    window.removeEventListener('pointerup', handlePointerUp);
  };

  const handlePointerDown = useCallback(() => {
    window.clearTimeout(timeoutBeforePlaying.current);
    timeoutBeforePlaying.current = -1;

    werePlaying.current = playing;
    pauseHandler();

    window.addEventListener('pointerup', handlePointerUp);
  }, [pauseHandler, handlePointerUp, playing]);

  return (
    <TimelineBase
      time={time}
      duration={duration}
      visible={visible}
      playing={playing}
      handleChange={handleChange}
      playHandler={playHandler}
      pauseHandler={pauseHandler}
      handlePointerDown={handlePointerDown}
    />
  );
};
